import type { OrgTreeNode, OrgEchelon } from '../api/keycloak-admin/org-config';
import type { StoredSectorTemplate } from '../api/keycloak-admin/org-config';
import { DEFAULT_MAP_VIEW } from '../config/defaults';

/** Build a tree node with defaults */
export function node(
  name: string,
  shortName: string,
  type: string,
  echelon: OrgEchelon,
  children: OrgTreeNode[] = [],
  plugins: string[] = [],
): OrgTreeNode {
  return {
    id: crypto.randomUUID(),
    name,
    shortName,
    type,
    echelon,
    children,
    plugins,
    integrations: [],
    mapView: { ...DEFAULT_MAP_VIEW },
  };
}

/** Create a seed template with built-in metadata */
function seed(
  sector: 'military',
  typeName: string,
  suggestedPlugins: string[],
  tree: OrgTreeNode[],
): StoredSectorTemplate {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    sector,
    typeName,
    suggestedPlugins,
    tree,
    builtIn: true,
    createdAt: now,
    updatedAt: now,
  };
}

/** All built-in military sector templates */
export function getMilitarySeedTemplates(): StoredSectorTemplate[] {
  return [
    seed('military', 'Space Force', ['space', 'missile-warn', 'force-protect'], [
      node('Field Command', 'SpOC', 'field-command', 'strategic', [
        node('Delta', 'DEL', 'delta', 'operational', [
          node('Squadron', 'SQD', 'squadron', 'tactical', [], ['space']),
          node('Squadron', 'SQD', 'squadron', 'tactical', [], ['space']),
        ]),
        node('Garrison', 'GAR', 'garrison', 'operational', [
          node('Security Forces', 'SFS', 'squadron', 'tactical', [], ['force-protect']),
          node('Base COP', 'COP', 'command', 'operational', [], ['space', 'force-protect']),
        ]),
      ]),
    ]),

    seed('military', 'Space Launch Delta 45', ['space', 'range-ops', 'force-protect', 'cyber'], [
      node('Space Launch Delta 45', 'SLD45', 'delta', 'operational', [
        node('Safety Group', 'SFG', 'group', 'operational', [
          node('1st Range Operations Sqd', '1 ROPS', 'squadron', 'tactical', [], ['range-ops']),
        ]),
        node('Operations Group', 'OG', 'group', 'operational', [
          node('5th Space Launch Sqd', '5 SLS', 'squadron', 'tactical', [], ['space', 'range-ops']),
          node('45th Weather Sqd', '45 WS', 'squadron', 'tactical', [], ['weather']),
        ]),
        node('Installation Support Group', 'MSG', 'group', 'operational', [
          node('45th Civil Engineer Sqd', '45 CES', 'squadron', 'tactical'),
          node('45th Force Support Sqd', '45 FSS', 'squadron', 'tactical'),
          node('45th Logistics Readiness Sqd', '45 LRS', 'squadron', 'tactical'),
          node('45th Security Forces Sqd', '45 SFS', 'squadron', 'tactical', [], ['force-protect']),
        ]),
        node('45th Medical Group', '45 MDG', 'group', 'operational'),
        node('645th Cyberspace Sqd', '645 CS', 'squadron', 'tactical', [], ['cyber']),
      ]),
    ]),

    seed('military', 'Space Launch Delta 30', ['space', 'range-ops', 'force-protect'], [
      node('Space Launch Delta 30', 'SLD30', 'delta', 'operational', [
        node('2nd Range Operations Sqd', '2 ROPS', 'squadron', 'tactical', [], ['range-ops']),
        node('2nd Space Launch Sqd', '2 SLS', 'squadron', 'tactical', [], ['space', 'range-ops']),
        node('4th Space Launch Sqd', '4 SLS', 'squadron', 'tactical', [], ['space', 'range-ops']),
        node('30th Operations Support Sqd', '30 OSS', 'squadron', 'tactical', [], ['space']),
        node('30th Security Forces Sqd', '30 SFS', 'squadron', 'tactical', [], ['force-protect']),
        node('30th Civil Engineer Sqd', '30 CES', 'squadron', 'tactical'),
        node('30th Logistics Readiness Sqd', '30 LRS', 'squadron', 'tactical'),
        node('30th Force Support Sqd', '30 FSS', 'squadron', 'tactical'),
        node('30th Contracting Sqd', '30 CONS', 'squadron', 'tactical'),
        node('30th Medical Group', '30 MDG', 'group', 'operational'),
        node('30th Comptroller Sqd', '30 CPTS', 'squadron', 'tactical'),
      ]),
    ]),

    seed('military', 'Army', ['ground', 'logistics', 'air-ops'], [
      node('Division', 'DIV', 'division', 'operational', [
        node('Brigade Combat Team', 'BCT', 'brigade', 'tactical', [], ['ground', 'logistics']),
        node('Brigade Combat Team', 'BCT', 'brigade', 'tactical', [], ['ground', 'logistics']),
        node('Division Artillery', 'DIVARTY', 'brigade', 'tactical', [], ['ground']),
        node('Combat Aviation Brigade', 'CAB', 'brigade', 'tactical', [], ['air-ops']),
      ]),
    ]),

    seed('military', 'Navy', ['maritime', 'air-ops', 'subsurface'], [
      node('Fleet', 'FLT', 'fleet', 'strategic', [
        node('Carrier Strike Group', 'CSG', 'task-group', 'tactical', [], ['maritime', 'air-ops']),
        node('Destroyer Squadron', 'DESRON', 'squadron', 'tactical', [], ['maritime', 'subsurface']),
      ]),
    ]),

    seed('military', 'Marine Corps', ['amphibious', 'ground', 'air-ops', 'maritime'], [
      node('MEF', 'MEF', 'mef', 'strategic', [
        node('MEU', 'MEU', 'meu', 'tactical', [], ['amphibious', 'ground', 'air-ops']),
        node('Marine Littoral Regiment', 'MLR', 'regiment', 'tactical', [], ['maritime', 'ground']),
      ]),
    ]),

    seed('military', 'Air Force', ['air-ops', 'isr'], [
      node('Wing', 'WG', 'wing', 'operational', [
        node('Operations Group', 'OG', 'group', 'tactical', [
          node('Fighter Squadron', 'FS', 'squadron', 'tactical', [], ['air-ops']),
          node('Fighter Squadron', 'FS', 'squadron', 'tactical', [], ['air-ops']),
        ]),
      ]),
    ]),

    seed('military', 'Coast Guard', ['maritime', 'sar', 'law-enforce'], [
      node('District', 'DIST', 'district', 'operational', [
        node('Sector', 'SEC', 'sector', 'tactical', [], ['maritime', 'sar', 'law-enforce']),
        node('Sector', 'SEC', 'sector', 'tactical', [], ['maritime', 'sar', 'law-enforce']),
        node('Air Station', 'AIRSTA', 'station', 'tactical', [], ['sar']),
      ]),
    ]),
  ];
}
